// Password validation utilities for enhanced security
// Implements stronger password requirements per security audit

export interface PasswordStrength {
  score: number; // 0-4 (weak to very strong)
  feedback: string[];
  isValid: boolean;
}

export const PASSWORD_REQUIREMENTS = {
  minLength: 12,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
  maxConsecutiveChars: 3,
  commonPasswords: [
    'password', '123456', 'password123', 'admin', 'letmein', 
    'welcome', 'monkey', '1234567890', 'qwerty', 'abc123',
    'Password1', 'password1', '123456789', 'welcome123'
  ]
};

export function validatePasswordStrength(password: string): PasswordStrength {
  const feedback: string[] = [];
  let score = 0;

  // Check minimum length
  if (password.length < PASSWORD_REQUIREMENTS.minLength) {
    feedback.push(`Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters long`);
  } else {
    score += 1;
  }

  // Check for uppercase letters
  if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
    feedback.push('Password must contain at least one uppercase letter');
  } else {
    score += 1;
  }

  // Check for lowercase letters
  if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
    feedback.push('Password must contain at least one lowercase letter');
  } else {
    score += 1;
  }

  // Check for numbers
  if (PASSWORD_REQUIREMENTS.requireNumbers && !/\d/.test(password)) {
    feedback.push('Password must contain at least one number');
  } else {
    score += 1;
  }

  // Check for special characters
  if (PASSWORD_REQUIREMENTS.requireSpecialChars && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    feedback.push('Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)');
  } else {
    score += 1;
  }

  // Check for consecutive repeated characters
  if (hasConsecutiveChars(password, PASSWORD_REQUIREMENTS.maxConsecutiveChars)) {
    feedback.push(`Password cannot contain more than ${PASSWORD_REQUIREMENTS.maxConsecutiveChars} consecutive identical characters`);
    score = Math.max(0, score - 1);
  }

  // Check against common passwords
  if (PASSWORD_REQUIREMENTS.commonPasswords.some(common => 
    password.toLowerCase().includes(common.toLowerCase())
  )) {
    feedback.push('Password cannot contain common words or patterns');
    score = Math.max(0, score - 2);
  }

  // Additional entropy checks
  if (password.length >= 16) score += 1;
  if (hasVariedCharacters(password)) score += 1;

  const isValid = feedback.length === 0 && score >= 4;

  return {
    score: Math.min(score, 4),
    feedback,
    isValid
  };
}

function hasConsecutiveChars(password: string, maxConsecutive: number): boolean {
  for (let i = 0; i <= password.length - maxConsecutive; i++) {
    const char = password[i];
    let count = 1;
    
    for (let j = i + 1; j < password.length && password[j] === char; j++) {
      count++;
      if (count > maxConsecutive) {
        return true;
      }
    }
  }
  return false;
}

function hasVariedCharacters(password: string): boolean {
  const charTypes = {
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    numbers: /\d/.test(password),
    special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
  };
  
  return Object.values(charTypes).filter(Boolean).length >= 3;
}

export function getPasswordStrengthText(score: number): string {
  switch (score) {
    case 0:
    case 1:
      return 'Very Weak';
    case 2:
      return 'Weak';
    case 3:
      return 'Fair';
    case 4:
      return 'Strong';
    default:
      return 'Very Weak';
  }
}

export function getPasswordStrengthColor(score: number): string {
  switch (score) {
    case 0:
    case 1:
      return 'text-red-600';
    case 2:
      return 'text-orange-500';
    case 3:
      return 'text-yellow-500';
    case 4:
      return 'text-green-600';
    default:
      return 'text-red-600';
  }
}